import type { ReactNode } from 'react';

/*
 * Declared here rather than beside the Footer that renders it. `types.ts` is the one module
 * in this package with no JSX in it, and apps whose tsconfig does not set `--jsx` import it
 * for `FlowConfig` — office-to-design's typecheck fails outright the moment this file pulls
 * in a `.tsx`.
 */
/**
 * What the help menu can offer. Every entry is optional, and an absent one leaves its row out
 * rather than showing something disabled — a menu of three where one never works reads as a
 * broken plugin, and a menu of two reads as a plugin with two things in it.
 */
export interface FooterHelp {
  /** Figma's id for the signed-in user, offered for a support conversation. */
  userId?: string | null | undefined;
  /**
   * Opens the feedback page. A CALLBACK, not a URL, and that is not fussiness: a plugin UI
   * runs in a sandboxed iframe, and the apps here open external links by asking the sandbox
   * to do it (`open-external`) rather than calling `window.open`. Taking a URL would have
   * baked in the mechanism that does not work everywhere.
   */
  onSendFeedback?: (() => void) | undefined;
  /** Shown only when there is a licence to manage, so a free user is not offered an empty dialog. */
  onManageLicense?: (() => void) | undefined;
}

/** Import lifecycle status, shared by every importer store. */
export type ImporterStatus =
  | 'idle'
  | 'converting'
  | 'placing'
  | 'done'
  | 'error';

export interface ImporterProgress {
  completed: number;
  total: number;
}

/**
 * The structural slice of a plugin's Zustand store that the shared flow reads.
 * Each app's store is a superset of this (it adds its own settings + setters).
 */
export interface ImporterStoreState {
  isReady: boolean;
  connectionError: string | null;
  skewMessage: string | null;
  file: File | null;
  status: ImporterStatus;
  progress: ImporterProgress | null;
  message: string | null;
  result: { count: number; skipped: number } | null;
  notice: string | null;
  setFile: (file: File | null) => void;
  startImport: () => void;
  cancel: () => void;
  reset: () => void;
  retryConnection: () => void;
}

/** A Zustand-style selector hook over a store state `S`. */
export type StoreHook<S> = <U>(selector: (state: S) => U) => U;

/**
 * Compile-time assertion that a Zustand store satisfies the {@link
 * ImporterStoreState} contract that {@link ImporterFlow} drives. Wrap the
 * `create(...)` call so the store file itself enforces the shared shape (and
 * catches drift early, independent of the `<ImporterFlow>` call site):
 *
 *   export const useImportStore = conformsToImporterFlow(create<ImportState>(...))
 *
 * It's the identity function at runtime — the constraint does all the work.
 */
export function conformsToImporterFlow<T extends StoreHook<ImporterStoreState>>(
  store: T,
): T {
  return store;
}

/** Static, per-app configuration + the settings UI for the flow. */
export interface FlowConfig {
  /*
   * NO BRANDING FIELDS. There was a `title`, a `subtitle` and a `footerLabel`, and together
   * they printed the plugin's name twice inside a window whose chrome Figma has already
   * labelled with it — and the subtitle repeated what the dropzone says better, in the place
   * the designer is actually looking.
   *
   * A plugin with something to say at the bottom can still pass `help`; the footer's label is
   * now a prop of the component rather than part of every app's config.
   */
  /** Plugin name, used as the error-logging context (e.g. "office-to-design"). */
  app: string;
  /** `accept` attribute for the file picker (e.g. ".ai,.eps"). */
  accept: string;
  /** Dropzone title (e.g. "Drop a PDF to begin"). */
  dropHint: string;
  /** Dropzone subtitle under the title (e.g. "We'll turn it into a Figma design…"). */
  dropSubhint?: string;
  /**
   * What the `?` menu in the footer offers. Omitted, the `?` is not shown at all.
   *
   * Every importer in the family wants the same three rows, and before this each one either
   * built its own or went without — illustrator and eps showed a bare label where pdf showed
   * a menu. Passing it here keeps the markup in one place and lets an app leave out a row it
   * cannot honour.
   */
  help?: FooterHelp;
  /** The app-specific settings controls (mode/scale/etc.). */
  settings: ReactNode;
  /** Short note in the file bar, e.g. "22 pgs" or "2500 × 1716". Omitted, the bar just names the file. */
  fileMeta?: string | undefined;
  /** A strip beside the controls — page thumbnails, a document preview. Omitted, the form fills the width. */
  preview?: ReactNode;
  /** What the import will do, e.g. "Importing 22 pages". */
  summary?: string | undefined;
  /** A quieter line under it, e.g. "About 48s". */
  subSummary?: string | undefined;
  /** An alternative to the primary button — "Import 1 artboard free" beside "Unlock with Pro". */
  secondaryAction?: { label: string; onClick: () => void } | undefined;
  /** Import is unavailable — an empty selection, typically. */
  importDisabled?: boolean | undefined;
  /** Primary action label. Defaults to "Import". */
  importLabel?: string;
  /** Noun for the placed units in the success summary. Defaults to "layer". */
  unitNoun?: string;
}
